import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classes from './CurrencyStep.module.scss';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import MainButton from '../../../../common/buttons/MainButton';
import TextButton from '../../../../common/buttons/TextButton/TextButton';
import { numberWithCommas } from '../../../../../utils/helpers';
import colors from '../../../../../scss/colors';

const autocompleteStyles = {
  '& .MuiSvgIcon-root': {
    color: colors.LIGHT_GREY,
    fontSize: '24px'
  }
};

export const currencyTextFieldStyles = (width, borderRadius, margin) => ({
  width,
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: borderRadius || '10px'
  },
  color: colors.BLACK_TXT,
  fontSize: '16px',
  fontWeight: '325',
  lineHeight: '19px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  },
  margin
});

export default function CurrencyStep({
  data,
  amount,
  setAmount,
  currency,
  setCurrency,
  currencies,
  error,
  fxRate,
  setFxRate,
  isCategoryMiles
}) {
  const { t } = useTranslation();
  const size = useWindowSize();
  const [showCurrency, setShowCurrency] = useState(false);

  return (
    <div className={classes.container}>
      {!isCategoryMiles ? (
        <>
          <p className={classes.text}>{t('dashboard.createclaim.steps.enterexpenseamount')}</p>
          <div className={classes.filedsContainer}>
            <TextField
              type="number"
              placeholder="0*"
              value={amount || ''}
              onChange={(e) => {
                setAmount(parseFloat(e.target.value));
              }}
              sx={currencyTextFieldStyles('125px', '10px 0 0 10px')}
            />
            <Autocomplete
              disableClearable
              value={currency || data?.PayCurrency}
              options={currencies}
              getOptionLabel={(option) => option.label || option || ''}
              isOptionEqualToValue={(option, value) => option.label === value}
              sx={autocompleteStyles}
              popupIcon={<ExpandMoreIcon />}
              forcePopupIcon
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  {option.label}
                </li>
              )}
              onChange={(event, newValue) => {
                setFxRate(0);
                setCurrency(newValue);
                if (newValue.label !== data?.PayCurrency) {
                  setShowCurrency(true);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={currencyTextFieldStyles(
                    size.width > responsiveSizeTimesheet ? '125px' : '125px',
                    '0 10px 10px 0'
                  )}
                />
              )}
            />
            {amount && currency && currency !== data?.PayCurrency ? (
              <p className={classes.expenseCurrencyValue}>
                = {numberWithCommas(parseFloat(amount * fxRate).toFixed(2))} {data?.PayCurrency}
              </p>
            ) : (
              ''
            )}
          </div>
          {size.width > responsiveSizeTimesheet && error && <p className={classes.error}>{error}</p>}
          {amount > 0 && currency && currency !== data?.PayCurrency && (
            <TextButton clickHandler={() => setShowCurrency(true)}>{t('common.general.updaterate')}</TextButton>
          )}
          <NotificationModal
            open={showCurrency}
            onClose={() => setShowCurrency(false)}
            paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
            height={size.width > responsiveSizeTimesheet ? '450px' : '600px'}
            gap="44px"
          >
            <p className={classes.expenseCurrencyText}>{t('dashboard.createclaim.steps.currencychange')}</p>
            <div className={classes.filedsContainer}>
              <TextField
                placeholder={t('dashboard.createclaim.steps.rate')}
                value={fxRate}
                type="number"
                onChange={(e) => {
                  setFxRate(parseFloat(e.target.value));
                }}
                sx={currencyTextFieldStyles('125px', '10px 0 0 10px', '0 10px 0 0')}
              />
              <span className={classes.expenseCurrencyText}>{data?.PayCurrency}</span>
            </div>
            <div>
              <MainButton
                rounded
                size="xsmall"
                forceWidth={size.width > responsiveSizeTimesheet ? '204px' : '100%'}
                action={() => {
                  setShowCurrency(false);
                }}
              >
                {t('common.general.save')}
              </MainButton>
            </div>
          </NotificationModal>
        </>
      ) : (
        <>
          <p className={classes.text}>{t('dashboard.createclaim.steps.entermiles')}</p>
          <div className={classes.filedsContainer}>
            <TextField
              type="number"
              placeholder="Miles*"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
              sx={currencyTextFieldStyles('125px', '10px 0 0 10px')}
            />
            <TextField
              type="number"
              placeholder="Rate*"
              value={fxRate}
              onChange={(e) => {
                setFxRate(e.target.value);
              }}
              sx={currencyTextFieldStyles('125px', '0 10px 10px 0')}
            />
            <p className={classes.expenseCurrencyValue}>
              = {numberWithCommas(parseFloat(amount * fxRate).toFixed(2))} {data?.PayCurrency}
            </p>
          </div>
          {size.width > responsiveSizeTimesheet && error && <p className={classes.error}>{error}</p>}
          {size.width > responsiveSizeTimesheet && !error && <p className={classes.error}>&nbsp;</p>}
        </>
      )}
    </div>
  );
}
